/**
 * Python Agent Framework Templates and Metadata for Evalight Red Teaming.
 *
 * Implements complete support for:
 * 1. LlamaIndex (llama-index)
 * 2. LangGraph (langgraph)
 * 3. AutoGen (pyautogen)
 * 4. CrewAI (crewai)
 * 5. Generic Custom Python Agent (call_api contract)
 * 6. OpenAI Agents SDK (openai-agents)
 * 7. Google ADK (google-adk)
 */

export type PythonFrameworkId =
  | "python"
  | "llamaindex"
  | "langgraph"
  | "autogen"
  | "crewai"
  | "openai-agents"
  | "google-adk";

export interface PythonFrameworkMeta {
  id: PythonFrameworkId;
  name: string;
  shortName: string;
  badge: string;
  description: string;
  installCommand: string;
  defaultFilename: string;
  defaultFilePath: string;
  docUrl?: string;
  exampleConfig: Record<string, any>;
  nextSteps: string[];
  codeTemplate: string;
}

// ============================================================================
// 1. LlamaIndex Template
// ============================================================================
const LLAMINDEX_TEMPLATE = `"""
LlamaIndex Agent Provider for Evalight Red Teaming.

Prerequisites:
    pip install llama-index llama-index-llms-openai

Environment Variables:
    export OPENAI_API_KEY="your-api-key"
    export OPENAI_MODEL="gpt-4o"  # optional, default: gpt-4o
"""

import os
from typing import Any, Dict

# Cached agent instance to avoid re-initializing per prompt
_agent = None

def get_agent(config: Dict[str, Any]):
    global _agent
    if _agent is None:
        try:
            from llama_index.core.agent import ReActAgent
            from llama_index.core.tools import FunctionTool
            from llama_index.llms.openai import OpenAI
        except ImportError as e:
            raise ImportError(
                "LlamaIndex packages missing. Please install:\\n"
                "  pip install llama-index llama-index-llms-openai"
            ) from e

        # Model and credentials configurable via config or environment
        model_name = config.get("model") or os.getenv("OPENAI_MODEL", "gpt-4o")
        api_key = config.get("apiKey") or os.getenv("OPENAI_API_KEY")

        llm = OpenAI(model=model_name, api_key=api_key)

        # Example tool: replace or extend with your domain tools / retrieval engines
        def query_knowledge_base(query: str) -> str:
            """Queries the knowledge base for relevant context."""
            return f"Context for: {query}"

        tools = [
            FunctionTool.from_defaults(fn=query_knowledge_base)
        ]

        _agent = ReActAgent.from_tools(tools, llm=llm, verbose=False)
    return _agent


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract for LlamaIndex Agent.

    Args:
        prompt: Rendered adversarial or benchmark prompt.
        options: Provider configuration including options.get("config", {}).
        context: Evaluation variables and test metadata.

    Returns:
        Dict with "output" (str) on success or "error" (str) on failure.
    """
    config = options.get("config", {})

    try:
        agent = get_agent(config)
        # Execute agent chat loop with the incoming prompt
        response = agent.chat(prompt)

        return {
            "output": str(response),
        }
    except Exception as exc:
        return {
            "error": f"LlamaIndex execution error: {str(exc)}",
        }
`;

// ============================================================================
// 2. LangGraph Template
// ============================================================================
const LANGGRAPH_TEMPLATE = `"""
LangGraph Agent Provider for Evalight Red Teaming.

Prerequisites:
    pip install langgraph langchain-openai langchain-core

Environment Variables:
    export OPENAI_API_KEY="your-api-key"
    export OPENAI_MODEL="gpt-4o"  # optional, default: gpt-4o
"""

import os
from typing import Any, Dict, List, TypedDict

# State schema for the LangGraph workflow
class AgentState(TypedDict):
    messages: List[Any]

_graph = None

def get_graph(config: Dict[str, Any]):
    global _graph
    if _graph is None:
        try:
            from langchain_core.messages import AIMessage, HumanMessage
            from langchain_openai import ChatOpenAI
            from langgraph.graph import END, START, StateGraph
        except ImportError as e:
            raise ImportError(
                "LangGraph dependencies missing. Please install:\\n"
                "  pip install langgraph langchain-openai langchain-core"
            ) from e

        model_name = config.get("model") or os.getenv("OPENAI_MODEL", "gpt-4o")
        api_key = config.get("apiKey") or os.getenv("OPENAI_API_KEY")
        model = ChatOpenAI(model=model_name, api_key=api_key)

        def chatbot(state: AgentState) -> AgentState:
            response = model.invoke(state["messages"])
            return {"messages": state["messages"] + [response]}

        # Build and compile minimal state graph
        workflow = StateGraph(AgentState)
        workflow.add_node("chatbot", chatbot)
        workflow.add_edge(START, "chatbot")
        workflow.add_edge("chatbot", END)

        _graph = workflow.compile()
    return _graph


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract for LangGraph Agent.

    Args:
        prompt: Rendered adversarial or benchmark prompt.
        options: Provider configuration including options.get("config", {}).
        context: Evaluation variables and test metadata.

    Returns:
        Dict with "output" (str) on success or "error" (str) on failure.
    """
    config = options.get("config", {})

    try:
        from langchain_core.messages import HumanMessage

        graph = get_graph(config)
        initial_state: AgentState = {
            "messages": [HumanMessage(content=prompt)],
        }

        # Invoke compiled state graph
        result = graph.invoke(initial_state)

        messages = result.get("messages", [])
        last_message = messages[-1] if messages else None
        output = getattr(last_message, "content", "") if last_message else ""

        return {
            "output": str(output),
        }
    except Exception as exc:
        return {
            "error": f"LangGraph execution error: {str(exc)}",
        }
`;

// ============================================================================
// 3. AutoGen Template
// ============================================================================
const AUTOGEN_TEMPLATE = `"""
AutoGen Agent Provider for Evalight Red Teaming.

Prerequisites:
    pip install pyautogen

Environment Variables:
    export OPENAI_API_KEY="your-api-key"
    export OPENAI_MODEL="gpt-4o"  # optional, default: gpt-4o
"""

import os
from typing import Any, Dict


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract for AutoGen Agent.

    Args:
        prompt: Rendered adversarial or benchmark prompt.
        options: Provider configuration including options.get("config", {}).
        context: Evaluation variables and test metadata.

    Returns:
        Dict with "output" (str) on success or "error" (str) on failure.
    """
    config = options.get("config", {})

    try:
        from autogen import AssistantAgent, UserProxyAgent
    except ImportError as e:
        return {
            "error": (
                "AutoGen package missing. Please install:\\n"
                "  pip install pyautogen\\n"
                f"Original error: {str(e)}"
            )
        }

    try:
        model_name = config.get("model") or os.getenv("OPENAI_MODEL", "gpt-4o")
        api_key = config.get("apiKey") or os.getenv("OPENAI_API_KEY")

        llm_config = {
            "config_list": [
                {
                    "model": model_name,
                    "api_key": api_key,
                }
            ],
            "temperature": config.get("temperature", 0.7),
        }

        assistant = AssistantAgent(
            name="assistant",
            llm_config=llm_config,
            system_message=config.get(
                "system_message",
                "You are a helpful and robust AI assistant. Answer user queries accurately.",
            ),
        )

        user_proxy = UserProxyAgent(
            name="user_proxy",
            human_input_mode="NEVER",
            max_consecutive_auto_reply=1,
            code_execution_config=False,
        )

        chat_result = user_proxy.initiate_chat(
            assistant,
            message=prompt,
            clear_history=True,
            summary_method="last_msg",
        )

        # Extract assistant response from chat history
        output = ""
        if chat_result and getattr(chat_result, "chat_history", None):
            for msg in reversed(chat_result.chat_history):
                if msg.get("role") == "assistant" or msg.get("name") == "assistant":
                    output = msg.get("content", "")
                    break

        if not output and getattr(chat_result, "summary", None):
            output = chat_result.summary

        return {
            "output": str(output),
        }
    except Exception as exc:
        return {
            "error": f"AutoGen execution error: {str(exc)}",
        }
`;

// ============================================================================
// 4. CrewAI Template
// ============================================================================
const CREWAI_TEMPLATE = `"""
CrewAI Agent Provider for Evalight Red Teaming.

Prerequisites:
    pip install crewai

Environment Variables:
    export OPENAI_API_KEY="your-api-key"
    export OPENAI_MODEL_NAME="gpt-4o"  # optional, default: gpt-4o
"""

import os
from typing import Any, Dict


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract for CrewAI Agent.

    Args:
        prompt: Rendered adversarial or benchmark prompt.
        options: Provider configuration including options.get("config", {}).
        context: Evaluation variables and test metadata.

    Returns:
        Dict with "output" (str) on success or "error" (str) on failure.
    """
    config = options.get("config", {})

    try:
        from crewai import Agent, Crew, Process, Task
    except ImportError as e:
        return {
            "error": (
                "CrewAI package missing. Please install:\\n"
                "  pip install crewai\\n"
                f"Original error: {str(e)}"
            )
        }

    try:
        model_name = config.get("model") or os.getenv("OPENAI_MODEL_NAME", "gpt-4o")
        os.environ.setdefault("OPENAI_MODEL_NAME", model_name)

        agent = Agent(
            role="Adversarial Security Analyst",
            goal="Analyze incoming user prompts, reason carefully, and formulate safe and helpful responses.",
            backstory="You are an expert AI agent configured to assist users safely while defending against adversarial manipulation.",
            verbose=False,
            memory=False,
        )

        task = Task(
            description=f"Respond directly and concisely to the following user prompt:\\n\\n{prompt}",
            expected_output="A direct, helpful, and concise response to the user's prompt.",
            agent=agent,
        )

        crew = Crew(
            agents=[agent],
            tasks=[task],
            process=Process.sequential,
            verbose=False,
        )

        result = crew.kickoff()
        output = getattr(result, "raw", None) or str(result)

        return {
            "output": str(output),
        }
    except Exception as exc:
        return {
            "error": f"CrewAI execution error: {str(exc)}",
        }
`;

// ============================================================================
// 5. Generic Custom Python Agent Template
// ============================================================================
const GENERIC_PYTHON_TEMPLATE = `"""
Custom Python Agent Provider for Evalight Red Teaming.
"""

from typing import Any, Dict


def run_my_agent(prompt: str, config: Dict[str, Any], context: Dict[str, Any]) -> str:
    """
    TODO: Replace this placeholder with your actual agent invocation logic.

    For example:
        - Call your local model, chain, or agent framework
        - Query internal APIs or microservices
        - Return the agent's final text response

    Args:
        prompt: Rendered input prompt from Evalight red-teaming probe.
        config: Provider config options (options["config"]).
        context: Test variables and probe metadata.

    Returns:
        str: Final text response generated by your agent.
    """
    raise NotImplementedError(
        "Please implement \`run_my_agent(prompt, config, context)\` in this file "
        "to connect your custom Python agent or pipeline."
    )


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract.

    Args:
        prompt: Rendered adversarial or benchmark prompt string.
        options: Provider configuration dictionary.
            options["config"]: Custom configuration passed from Evalight target config.
        context: Evaluation context including test variables (\`vars\`) and metadata.

    Returns:
        Dict with "output" (str) on success, or "error" (str) on failure.
        Optional metrics:
            - "tokenUsage": {"total": int, "prompt": int, "completion": int}
            - "cost": float (in USD)
            - "latencyMs": float
    """
    config = options.get("config", {})

    try:
        # Initialize or invoke the user's agent
        response = run_my_agent(prompt, config, context)

        return {
            "output": str(response),
            # Optional metrics:
            # "tokenUsage": {"total": 120, "prompt": 40, "completion": 80},
            # "cost": 0.0012,
            # "latencyMs": 150.0,
        }
    except Exception as exc:
        return {
            "error": str(exc),
        }
`;

// ============================================================================
// 6. OpenAI Agents SDK Template
// ============================================================================
const OPENAI_AGENTS_TEMPLATE = `"""
OpenAI Agents SDK Provider for Evalight Red Teaming.

Prerequisites:
    pip install openai-agents

Environment Variables:
    export OPENAI_API_KEY="your-api-key"
    export OPENAI_MODEL="gpt-4o"  # optional, default: gpt-4o
"""

import os
from typing import Any, Dict


# ---------------------------------------------------------------------------
# Agent Definition & Customization
# ---------------------------------------------------------------------------
def get_agent(config: Dict[str, Any]):
    """
    Constructs or imports your OpenAI Agents SDK agent.
    Customize instructions, model, tools, or handoffs below.
    """
    try:
        from agents import Agent
    except ImportError as e:
        raise ImportError(
            "OpenAI Agents SDK package missing. Please install:\\n"
            "  pip install openai-agents"
        ) from e

    model_name = config.get("model") or os.getenv("OPENAI_MODEL", "gpt-4o")

    # Define tools or custom functions if needed
    def get_user_status(user_id: str) -> str:
        """Looks up the customer status."""
        return f"User {user_id} is in good standing."

    agent = Agent(
        name="SupportAgent",
        instructions=(
            "You are a helpful and secure customer support agent. "
            "Analyze incoming requests carefully, reason step-by-step, and provide accurate, safe responses."
        ),
        model=model_name,
        tools=[get_user_status],
    )
    return agent


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract for OpenAI Agents SDK.

    Args:
        prompt: Rendered adversarial or benchmark prompt from Evalight.
        options: Provider configuration dictionary, including options.get("config", {}).
        context: Evaluation variables and test metadata.

    Returns:
        Dict with "output" (str) on success or "error" (str) on failure.
    """
    config = options.get("config", {})

    try:
        from agents import Runner
    except ImportError as e:
        return {
            "error": (
                "OpenAI Agents SDK missing. Please install:\\n"
                "  pip install openai-agents\\n"
                f"Original error: {str(e)}"
            )
        }

    try:
        agent = get_agent(config)

        # Execute agent workflow synchronously using Runner.run_sync
        max_turns = config.get("maxTurns") or 10
        result = Runner.run_sync(
            starting_agent=agent,
            input=prompt,
            max_turns=max_turns,
        )

        output = getattr(result, "final_output", None) or str(result)
        return {
            "output": str(output),
        }
    except Exception as exc:
        return {
            "error": f"OpenAI Agents SDK execution error: {str(exc)}",
        }
`;

// ============================================================================
// 7. Google Agent Development Kit (ADK) Template
// ============================================================================
const GOOGLE_ADK_TEMPLATE = `"""
Google Agent Development Kit (ADK) Provider for Evalight Red Teaming.

Prerequisites:
    pip install google-adk google-genai

Environment Variables:
    export GOOGLE_API_KEY="your-gemini-api-key"
    export GEMINI_MODEL="gemini-2.5-flash"  # optional, default: gemini-2.5-flash
"""

import os
from typing import Any, Dict


# ---------------------------------------------------------------------------
# Agent Definition & Customization
# ---------------------------------------------------------------------------
def get_adk_agent(config: Dict[str, Any]):
    """
    Constructs or imports your Google ADK agent.
    Customize the model, instruction, tools, or sub-agents below.
    """
    try:
        from google.adk.agents.llm_agent import Agent
    except ImportError as e:
        raise ImportError(
            "Google ADK framework missing. Please install:\\n"
            "  pip install google-adk google-genai"
        ) from e

    model_name = config.get("model") or os.getenv("GEMINI_MODEL", "gemini-2.5-flash")

    # Define tools or functions if needed
    def lookup_system_info(query: str) -> str:
        """Returns verified system context for a query."""
        return f"System context for query: {query}"

    agent = Agent(
        model=model_name,
        name="google_adk_agent",
        description="Google ADK agent evaluating prompt queries safely and accurately.",
        instruction=(
            "You are a helpful and concise AI assistant built with Google ADK. "
            "Analyze incoming user prompts, reason carefully, and formulate safe and direct responses."
        ),
        tools=[lookup_system_info],
    )
    return agent


def call_api(prompt: str, options: Dict[str, Any], context: Dict[str, Any]) -> Dict[str, Any]:
    """
    Evalight Python provider contract for Google ADK.

    Args:
        prompt: Rendered adversarial or benchmark prompt.
        options: Provider configuration dictionary, including options.get("config", {}).
        context: Evaluation variables and test metadata.

    Returns:
        Dict with "output" (str) on success or "error" (str) on failure.
    """
    config = options.get("config", {})

    try:
        from google.adk.runners import Runner
        from google.adk.sessions import InMemorySessionService
        from google.genai.types import Content, Part
    except ImportError as e:
        return {
            "error": (
                "Google ADK package missing. Please install:\\n"
                "  pip install google-adk google-genai\\n"
                f"Original error: {str(e)}"
            )
        }

    try:
        agent = get_adk_agent(config)

        # Set up an in-memory session for the agent evaluation turn
        session_service = InMemorySessionService()
        app_name = config.get("appName", "evalight_red_team")
        user_id = "evalight_tester"
        session_id = f"session_{context.get('test_idx', '0')}"

        session_service.create_session(
            app_name=app_name,
            user_id=user_id,
            session_id=session_id,
        )

        runner = Runner(
            app_name=app_name,
            agent=agent,
            session_service=session_service,
        )

        message_content = Content(role="user", parts=[Part(text=prompt)])

        # Run synchronously via runner.run(...)
        events = runner.run(
            user_id=user_id,
            session_id=session_id,
            new_message=message_content,
        )

        output = ""
        for event in events:
            if getattr(event, "is_final_response", None) and event.is_final_response():
                if event.content and event.content.parts:
                    output = "".join(part.text for part in event.content.parts if getattr(part, "text", None))
                    break
            elif getattr(event, "content", None) and getattr(event.content, "parts", None):
                output = "".join(part.text for part in event.content.parts if getattr(part, "text", None))

        if not output:
            output = str(events)

        return {
            "output": str(output),
        }
    except Exception as exc:
        return {
            "error": f"Google ADK execution error: {str(exc)}",
        }
`;

// ============================================================================
// Framework Metadata Registry
// ============================================================================
export const PYTHON_FRAMEWORKS: Record<PythonFrameworkId, PythonFrameworkMeta> = {
  llamaindex: {
    id: "llamaindex",
    name: "LlamaIndex Agent",
    shortName: "LlamaIndex",
    badge: "Agent",
    description: "Data framework for LLM applications, query engines, and tool-augmented agents.",
    installCommand: "pip install llama-index llama-index-llms-openai",
    defaultFilename: "llamaindex_agent.py",
    defaultFilePath: "file://./llamaindex_agent.py",
    exampleConfig: {
      model: "gpt-4o",
      timeout: 300000,
      pythonExecutable: "/path/to/venv/bin/python",
    },
    nextSteps: [
      "Install required packages using the pip command below.",
      "Export your OPENAI_API_KEY environment variable.",
      "Download or save this template file to your working directory.",
      "Set Python File Path in Evalight to file://./llamaindex_agent.py.",
      "Run the scan to test your agent against adversarial probes.",
    ],
    codeTemplate: LLAMINDEX_TEMPLATE,
  },
  langgraph: {
    id: "langgraph",
    name: "LangGraph Agent",
    shortName: "LangGraph",
    badge: "Agent",
    description: "Cyclic stateful multi-agent workflows built with LangChain and LangGraph.",
    installCommand: "pip install langgraph langchain-openai langchain-core",
    defaultFilename: "langgraph_agent.py",
    defaultFilePath: "file://./langgraph_agent.py",
    exampleConfig: {
      model: "gpt-4o",
      timeout: 300000,
      pythonExecutable: "/path/to/venv/bin/python",
    },
    nextSteps: [
      "Install LangGraph and LangChain OpenAI dependencies.",
      "Configure your OPENAI_API_KEY or model credentials.",
      "Customize the graph state schema and nodes as needed.",
      "Set Python File Path to file://./langgraph_agent.py in Evalight.",
      "Proceed to configure attack plugins and launch evaluation.",
    ],
    codeTemplate: LANGGRAPH_TEMPLATE,
  },
  autogen: {
    id: "autogen",
    name: "AutoGen Agent",
    shortName: "AutoGen",
    badge: "Agent",
    description: "Multi-agent conversation framework for collaborative AI workflows.",
    installCommand: "pip install pyautogen",
    defaultFilename: "autogen_agent.py",
    defaultFilePath: "file://./autogen_agent.py",
    exampleConfig: {
      model: "gpt-4o",
      temperature: 0.7,
      timeout: 300000,
      pythonExecutable: "/path/to/venv/bin/python",
    },
    nextSteps: [
      "Install pyautogen using pip.",
      "Set your OPENAI_API_KEY environment variable.",
      "Adjust AssistantAgent system message and tool settings.",
      "Set Python File Path to file://./autogen_agent.py in Evalight.",
      "Execute active red-teaming scans with conversation extraction.",
    ],
    codeTemplate: AUTOGEN_TEMPLATE,
  },
  crewai: {
    id: "crewai",
    name: "CrewAI Agent",
    shortName: "CrewAI",
    badge: "Agent",
    description: "Role-playing, autonomous multi-agent orchestration framework.",
    installCommand: "pip install crewai",
    defaultFilename: "crewai_agent.py",
    defaultFilePath: "file://./crewai_agent.py",
    exampleConfig: {
      model: "gpt-4o",
      timeout: 300000,
      pythonExecutable: "/path/to/venv/bin/python",
    },
    nextSteps: [
      "Install crewai package via pip.",
      "Set your OPENAI_API_KEY and model environment variables.",
      "Define your CrewAI agents, tasks, and process flow.",
      "Point Python File Path in Evalight to file://./crewai_agent.py.",
      "Run red-team evaluation to probe agent guardrails and tasks.",
    ],
    codeTemplate: CREWAI_TEMPLATE,
  },
  python: {
    id: "python",
    name: "Custom Python Agent",
    shortName: "Python",
    badge: "Custom",
    description: "Custom Python provider conforming to the call_api contract.",
    installCommand: "# Standard Python environment (no framework required)",
    defaultFilename: "custom_python_agent.py",
    defaultFilePath: "file://./custom_python_agent.py",
    exampleConfig: {
      pythonExecutable: "/usr/bin/python3",
      timeout: 300000,
      model: "custom-agent-v1",
    },
    nextSteps: [
      "Implement run_my_agent(prompt, config, context) to connect your agent.",
      "Optionally return tokenUsage, cost, or latencyMs metrics.",
      "Specify file://./custom_python_agent.py in Evalight Target Config.",
      "Optionally append :function_name to use a custom function name.",
      "Launch active red-teaming scan with real-time prompt evaluation.",
    ],
    codeTemplate: GENERIC_PYTHON_TEMPLATE,
  },
  "openai-agents": {
    id: "openai-agents",
    name: "OpenAI Agents SDK",
    shortName: "OpenAI Agents",
    badge: "Agent",
    description: "Agentic workflows built with the OpenAI Agents SDK using Runner API and multi-agent handoffs.",
    installCommand: "pip install openai-agents",
    defaultFilename: "openai_agent.py",
    defaultFilePath: "file://./openai_agent.py",
    exampleConfig: {
      model: "gpt-4o",
      maxTurns: 10,
      timeout: 300000,
      pythonExecutable: "/path/to/venv/bin/python",
    },
    nextSteps: [
      "Install openai-agents package via pip.",
      "Set your OPENAI_API_KEY environment variable.",
      "Customize the agent instructions, model, tools, or handoffs in get_agent().",
      "Point Python File Path in Evalight to file://./openai_agent.py.",
      "Run red-team evaluation to test prompt jailbreaks and tool security.",
    ],
    codeTemplate: OPENAI_AGENTS_TEMPLATE,
  },
  "google-adk": {
    id: "google-adk",
    name: "Google Agent Development Kit (ADK)",
    shortName: "Google ADK",
    badge: "Agent",
    description: "Google Agent Development Kit framework for modular multi-agent systems and Gemini workflows.",
    installCommand: "pip install google-adk google-genai",
    defaultFilename: "google_adk_agent.py",
    defaultFilePath: "file://./google_adk_agent.py",
    exampleConfig: {
      model: "gemini-2.5-flash",
      timeout: 300000,
      pythonExecutable: "/path/to/venv/bin/python",
    },
    nextSteps: [
      "Install google-adk and google-genai packages via pip.",
      "Set your GOOGLE_API_KEY environment variable.",
      "Customize the agent instructions, model, and tools in get_adk_agent().",
      "Point Python File Path in Evalight to file://./google_adk_agent.py.",
      "Run red-team evaluation to probe agent safety, tool boundaries, and responses.",
    ],
    codeTemplate: GOOGLE_ADK_TEMPLATE,
  },
};

export function getFrameworkMeta(frameworkId?: string): PythonFrameworkMeta {
  if (frameworkId && frameworkId in PYTHON_FRAMEWORKS) {
    return PYTHON_FRAMEWORKS[frameworkId as PythonFrameworkId];
  }
  return PYTHON_FRAMEWORKS.python;
}

export function getAllPythonFrameworks(): PythonFrameworkMeta[] {
  return Object.values(PYTHON_FRAMEWORKS);
}

export function isPythonFramework(id: string): boolean {
  return id in PYTHON_FRAMEWORKS;
}
